import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { inputsMaxLengths } from '../../../constants';
import styles from './FormContent.module.scss';
import { Button, Input, Text } from 'components';
import { appActions } from 'store/app/slice';
import { usersActions } from 'store/users/slice';
import { useDispatch } from 'store/utils';
import { UserRole } from 'types';
import { onlyLatinLettersSymbolsNumbers } from 'utils';

const schema = yup.object({
  username: yup
    .string()
    .matches(onlyLatinLettersSymbolsNumbers, {
      message: 'Only Latin Alphabet is allowed',
    })
    .matches(/^(?!\s)(?!.*\s$)/, {
      message: 'No leading and trailing spaces are allowed',
    })
    .required('The field is empty'),
  email: yup
    .string()
    .email('The text is not an email')
    .matches(/^.+@([a-zA-Z0-9.\-_])+(\.[a-zA-Z]{2,})$/, {
      message: 'The text is not an email',
    })
    .required('The field is empty'),
  role: yup.string().required('Role is required'),
});

type Props = {
  setUserEmail(value: string): void;
  setUserCreated(value: boolean): void;
  setShowModalError(value: boolean): void;
};

const currentRoleByEnum = (selectedRole: string) => {
  if (selectedRole === 'STUDIO') {
    return UserRole.CLINIC;
  }
  return selectedRole as UserRole;
};

export const FormContent = ({ setShowModalError, setUserCreated, setUserEmail }: Props) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({ resolver: yupResolver(schema) });

  const watchedUsername: string = watch('username');
  const watchedEmail: string = watch('email');
  const watchedRole = watch('role');

  useEffect(() => {
    setUserEmail(watchedEmail);
  }, [watchedEmail, setUserEmail]);

  const handleRoleButtonClick: React.MouseEventHandler<HTMLInputElement> = (event) => {
    const target = event.target as HTMLInputElement;
    setValue('role', target.value);
  };

  const onSubmit = handleSubmit(async (formData) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const wrappedResult = dispatch(
        usersActions.createUser({
          name: formData.username,
          email: formData.email,
          role: currentRoleByEnum(formData.role),
        }),
      );
      const { isCreated } = await wrappedResult.unwrap();
      isCreated && setUserCreated(true);
      dispatch(usersActions.getUsers());
      dispatch(appActions.setModalErrorText({ name: 'createUserError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'createUserError', errorText: error?.title }));
      setShowModalError(true);
    } finally {
      setIsLoading(false);
    }
  });

  const close = () => {
    dispatch(appActions.setIsModalOpened({ name: 'createUser', isOpened: false }));
  };

  return (
    <div className={styles.formContainer}>
      <form className={styles.form}>
        <Input
          inputName="username"
          labelText="Name of the user"
          register={register}
          registerOptions={{ required: true, maxLength: inputsMaxLengths.username }}
          autoComplete="off"
          placeholder="Enter user name"
          maxLength={inputsMaxLengths.username}
          currentValue={watchedUsername}
          setValue={setValue}
          errorMsg={errors.username?.message}
          characterCounter
        />
        <Input
          inputName="email"
          labelText="Email"
          register={register}
          registerOptions={{ required: true }}
          autoComplete="off"
          placeholder="Enter user email"
          errorMsg={errors.email?.message}
        />
        <div className={styles.inputItem}>
          <Text type="caption">Select user role</Text>
          <div className={styles.roles}>
            {Object.values(UserRole).map((role: UserRole) => (
              <input
                onClick={handleRoleButtonClick}
                type="button"
                className={`${styles.roleBtn} ${currentRoleByEnum(watchedRole) === role ? styles.selected : ''}`}
                value={role === UserRole.CLINIC ? 'STUDIO' : role}
                key={role}
              />
            ))}
          </div>
          <div className={styles.inputItemInfo}>
            <Text className={styles.error} type="caption">
              {errors.role?.message}
            </Text>
          </div>
        </div>
      </form>
      <div className={styles.buttonRow}>
        <Button isLink onClick={close}>
          Cancel
        </Button>
        <Button onClick={onSubmit}>Create user</Button>
      </div>
    </div>
  );
};
